import { useEffect, useState } from "react";
import { Plus, Edit3, Trash2, X, Megaphone, ToggleLeft, ToggleRight, Copy, Eye } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import usePagedRows from "../components/usePagedRows";
import Pagination from "../components/Pagination";
import "../admin-affiliate-premium.css";

const BLANK = {
  name: "", code: "", type: "deposit_bonus", bonus_pct: 0, bonus_amount: 0,
  min_deposit: 0, max_bonus: 0, free_spin_count: 0, usage_limit: 0,
  expires_at: "", target_tier: "", landing_slug: "", description: "", status: "active",
};

export default function AffiliateCampaigns() {
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState(BLANK);
  const [editId, setEditId] = useState(null);
  const [open, setOpen] = useState(false);
  const [filter, setFilter] = useState("");
  const paged = usePagedRows(rows, 12, [filter]);

  const load = async () => {
    try {
      const r = await api.get("/admin/aff-campaigns" + (filter ? `?status=${filter}` : ""));
      setRows(r.data?.rows || []);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  useEffect(() => { load(); }, [filter]);

  const save = async () => {
    try {
      if (editId) {
        await api.patch(`/admin/aff-campaigns/${editId}`, form);
        toast.success("Campaign updated");
      } else {
        await api.post("/admin/aff-campaigns", form);
        toast.success("Campaign created");
      }
      setOpen(false); setForm(BLANK); setEditId(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this campaign?")) return;
    try { await api.delete(`/admin/aff-campaigns/${id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const toggleStatus = async (c) => {
    const next = c.status === "active" ? "paused" : "active";
    try { await api.patch(`/admin/aff-campaigns/${c.id}`, { status: next }); toast.success(`Status: ${next}`); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const startEdit = (c) => {
    setForm({ ...BLANK, ...c, expires_at: (c.expires_at || "").slice(0, 10) });
    setEditId(c.id);
    setOpen(true);
  };

  const copy = (t) => navigator.clipboard.writeText(t).then(() => toast.success("Copied"));

  return (
    <div className="ap-bg p-4 lg:p-6">
      <div className="flex items-end justify-between mb-5 flex-wrap gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-[0.32em] text-amber-300 font-bold mb-1">MARKETING</div>
          <div className="text-3xl font-black ap-gold-text leading-tight">Promo Campaigns</div>
          <div className="text-white/55 text-sm mt-1">Launch bonus campaigns and track click-to-conversion performance.</div>
        </div>
        <button onClick={() => { setForm(BLANK); setEditId(null); setOpen(true); }} className="ap-btn-gold inline-flex items-center gap-2 px-4 h-10 rounded-xl text-[12px] font-bold" data-testid="new-campaign-btn">
          <Plus size={14} /> New Campaign
        </button>
      </div>

      <div className="flex gap-2 mb-4">
        {["", "active", "paused", "ended", "draft"].map((s) => (
          <button key={s || "all"} onClick={() => setFilter(s)} className={`px-3 h-8 rounded-lg text-[11px] font-bold uppercase tracking-[0.18em] ${filter === s ? "ap-btn-gold" : "ap-btn-ghost"}`}>
            {s || "All"}
          </button>
        ))}
      </div>

      <div className="ap-glass overflow-hidden">
        <div className="ap-table overflow-x-auto">
          <table>
            <thead>
              <tr>
                <th>Name</th><th>Code</th><th>Type</th><th>Bonus</th>
                <th>Clicks</th><th>Conv.</th><th>CTR</th><th>Status</th><th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <tr><td colSpan={9} className="text-center py-6 text-white/45">No campaigns yet — create the first one.</td></tr>
              ) : paged.visible.map((c) => (
                <tr key={c.id}>
                  <td>
                    <div className="font-bold text-white">{c.name}</div>
                    <div className="text-[11px] text-white/45 truncate max-w-[280px]">{c.description}</div>
                  </td>
                  <td>
                    <div className="inline-flex items-center gap-2">
                      <code className="text-amber-100">{c.code}</code>
                      <button onClick={() => copy(c.code)} className="ap-btn-ghost p-1 rounded-md"><Copy size={11} /></button>
                    </div>
                  </td>
                  <td>{c.type}</td>
                  <td>
                    {c.bonus_pct > 0 && <div>{c.bonus_pct}%</div>}
                    {c.bonus_amount > 0 && <div>৳{c.bonus_amount}</div>}
                    {c.free_spin_count > 0 && <div>{c.free_spin_count} spins</div>}
                  </td>
                  <td>{c.stats?.clicks || 0}</td>
                  <td className="text-emerald-300 font-bold">{c.stats?.conversions || 0}</td>
                  <td>{(c.stats?.ctr || 0).toFixed(1)}%</td>
                  <td>
                    <button onClick={() => toggleStatus(c)} className="inline-flex items-center gap-1 px-2 h-7 rounded-md text-[10px] font-bold uppercase tracking-[0.18em]" style={c.status === "active" ? { color: "#6ee7b7", background: "rgba(16,185,129,0.12)", border: "1px solid rgba(16,185,129,0.35)" } : { color: "#fcd34d", background: "rgba(244,180,49,0.12)", border: "1px solid rgba(244,180,49,0.35)" }}>
                      {c.status === "active" ? <ToggleRight size={11} /> : <ToggleLeft size={11} />} {c.status}
                    </button>
                  </td>
                  <td>
                    <div className="flex items-center gap-1">
                      <button onClick={() => startEdit(c)} className="ap-btn-ghost p-1.5 rounded-md"><Edit3 size={12} /></button>
                      <button onClick={() => remove(c.id)} className="ap-btn-ghost p-1.5 rounded-md text-rose-300"><Trash2 size={12} /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <Pagination total={paged.totalRows} page={paged.page} pageSize={paged.pageSize} onChange={paged.setPage} testIdPrefix="aff-campaigns-pg" />
      </div>

      {open && (
        <div className="ap-modal-overlay" onClick={() => setOpen(false)}>
          <div className="ap-modal" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <div className="text-[10px] uppercase tracking-[0.28em] text-amber-300 font-bold">{editId ? "EDIT CAMPAIGN" : "NEW CAMPAIGN"}</div>
              <button onClick={() => setOpen(false)} className="ap-btn-ghost p-2 rounded-lg"><X size={14} /></button>
            </div>
            <h3 className="text-xl font-black ap-gold-text mb-3">Configure Promo Campaign</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <Field label="Name"><input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="ap-input" /></Field>
              <Field label="Code (optional)"><input value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} className="ap-input" placeholder="auto-gen if blank" /></Field>
              <Field label="Type">
                <select value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} className="ap-input">
                  {["deposit_bonus", "free_spin", "cashback", "first_deposit", "vip_invite"].map(t => <option key={t} value={t}>{t}</option>)}
                </select>
              </Field>
              <Field label="Status">
                <select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} className="ap-input">
                  {["active", "paused", "ended", "draft"].map(t => <option key={t} value={t}>{t}</option>)}
                </select>
              </Field>
              <Field label="Bonus %"><input type="number" value={form.bonus_pct} onChange={(e) => setForm({ ...form, bonus_pct: Number(e.target.value) })} className="ap-input" /></Field>
              <Field label="Bonus Amount (৳)"><input type="number" value={form.bonus_amount} onChange={(e) => setForm({ ...form, bonus_amount: Number(e.target.value) })} className="ap-input" /></Field>
              <Field label="Min Deposit (৳)"><input type="number" value={form.min_deposit} onChange={(e) => setForm({ ...form, min_deposit: Number(e.target.value) })} className="ap-input" /></Field>
              <Field label="Max Bonus (৳)"><input type="number" value={form.max_bonus} onChange={(e) => setForm({ ...form, max_bonus: Number(e.target.value) })} className="ap-input" /></Field>
              <Field label="Free Spins"><input type="number" value={form.free_spin_count} onChange={(e) => setForm({ ...form, free_spin_count: Number(e.target.value) })} className="ap-input" /></Field>
              <Field label="Usage Limit"><input type="number" value={form.usage_limit} onChange={(e) => setForm({ ...form, usage_limit: Number(e.target.value) })} className="ap-input" /></Field>
              <Field label="Expires At"><input type="date" value={(form.expires_at || "").slice(0, 10)} onChange={(e) => setForm({ ...form, expires_at: e.target.value })} className="ap-input" /></Field>
              <Field label="Target Tier"><input value={form.target_tier} onChange={(e) => setForm({ ...form, target_tier: e.target.value })} className="ap-input" placeholder="e.g. Gold (optional)" /></Field>
              <div className="md:col-span-2">
                <Field label="Description"><textarea rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="ap-input resize-none" /></Field>
              </div>
            </div>
            <button onClick={save} className="ap-btn-gold mt-4 px-5 h-11 rounded-xl text-[13px] font-bold inline-flex items-center gap-2" data-testid="save-campaign-btn">
              <Megaphone size={14} /> {editId ? "Save Changes" : "Create Campaign"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-white/55 mb-1.5">{label}</div>
      {children}
    </div>
  );
}
